import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber, ellipsis } from '@fabric-ui/utils';
import { Box, Tooltip, useTheme } from '@mui/material';
import WidgetTooltip, { TooltipDivider } from './WidgetTooltip';

interface BoxData {
  label: string;
  value: number;
}

interface TreemapWidgetProps {
  data: BoxData[];
  selected: string;
  onSelect: (label: string) => void;
}

const TreemapWidget = ({ data, selected, onSelect }: TreemapWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const theme = useTheme();

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return () => {};

    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(element);
    return () => resizeObserver.unobserve(element);
  }, []);

  const boxes = useMemo(() => calculateTreemapLayout(data || [], dimensions.width, dimensions.height), [data, dimensions]);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', flex: 1 }}>
      {!boxes.length ? (
        <Box sx={{ ...flex.center, height: '100%' }}>
          <NoDataFoundWidget />
        </Box>
      ) : (
        boxes.map(({ item, style }) => {
          const isSelected = selected === item.label;
          return (
            <Tooltip
              key={item.label}
              title={<CustomTooltip active payload={[{ value: item.value }]} label={item.label} />}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'transparent',
                  },
                },
              }}>
              <Box
                onClick={() => onSelect(item.label)}
                sx={{
                  position: 'absolute',
                  backgroundColor: isSelected ? theme.palette.colors.primary[600] : theme.palette.colors.primary[150],
                  border: '2px solid var(--color-content-immutable-white)',
                  ...flex.colJustifyBetween,
                  padding: '8px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  ...style,
                }}>
                <Box
                  sx={{
                    color: isSelected ? theme.palette.colors.neutrals[150] : 'var(--color-content-base-primary)',
                    ...ellipsis,
                  }}>
                  {item.label}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'flex-end',
                    color: isSelected ? theme.palette.colors.neutrals[200] : theme.palette.colors.neutrals[550],
                    fontWeight: 600,
                    fontSize: '13px',
                  }}>
                  {abbreviateNumber(item.value)}
                </Box>
              </Box>
            </Tooltip>
          );
        })
      )}
    </Box>
  );
};

const calculateTreemapLayout = (data: BoxData[], containerWidth: number, containerHeight: number) => {
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  if (totalValue === 0) {
    return [];
  }

  const MIN_PROPORTION = 0.01;

  const layoutData = data.map(item => {
    const proportion = item.value / totalValue;
    const modifiedValue = item.value > 0 && proportion < MIN_PROPORTION ? totalValue * MIN_PROPORTION : item.value;

    return {
      originalItem: item,
      modifiedValue,
    };
  });

  const newTotalValue = layoutData.reduce((sum, item) => sum + item.modifiedValue, 0);

  const { boxes } = layoutData.reduce(
    (acc, layoutItem) => {
      const proportion = layoutItem.modifiedValue / newTotalValue;
      const area = proportion * containerWidth * containerHeight;

      const isHorizontal = acc.remainingWidth > acc.remainingHeight;

      const calculatedWidth = isHorizontal ? area / acc.remainingHeight : acc.remainingWidth;
      const calculatedHeight = isHorizontal ? acc.remainingHeight : area / acc.remainingWidth;

      const width = Math.min(calculatedWidth, acc.remainingWidth);
      const height = Math.min(calculatedHeight, acc.remainingHeight);

      const newBox = {
        item: layoutItem.originalItem,
        style: {
          left: acc.currentX,
          top: acc.currentY,
          width,
          height,
        },
      };

      return {
        boxes: [...acc.boxes, newBox],
        currentX: acc.currentX + (isHorizontal ? width : 0),
        currentY: acc.currentY + (isHorizontal ? 0 : height),
        remainingWidth: acc.remainingWidth - (isHorizontal ? width : 0),
        remainingHeight: acc.remainingHeight - (isHorizontal ? 0 : height),
      };
    },
    {
      boxes: [] as { item: BoxData; style: React.CSSProperties }[],
      currentX: 0,
      currentY: 0,
      remainingWidth: containerWidth,
      remainingHeight: containerHeight,
    }
  );

  return boxes;
};

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;
  const theme = useTheme();

  if (active && payload?.length) {
    return (
      <WidgetTooltip active={active} payload={payload}>
        <Box
          sx={{
            ...flex.justifyBetweenCenter,
            fontWeight: 600,
            color: 'var(--color-content-immutable-white)',
          }}>
          <div>Tactic</div>
          <div>{label}</div>
        </Box>
        <TooltipDivider />
        <Box sx={{ ...flex.justifyBetweenCenter }}>
          <Box sx={{ color: theme.palette.colors.neutrals[350] }}>Alerts Count</Box>
          <Box sx={{ fontWeight: 600, color: 'var(--color-content-immutable-white)' }}>{payload[0].value.toLocaleString()}</Box>
        </Box>
      </WidgetTooltip>
    );
  }

  return null;
};

export default TreemapWidget;
